/**
 * The run-status vocabulary: the colour and the human label for every status the API can report.
 *
 * EXTRACTED FROM `GlobalRunHistory` so the shared Info affordance can use it without importing a
 * page. The panels moved into `components/OutboundInfoButton`, which the page then renders -- a
 * component importing back from that page would be an import cycle, and a cycle whose module
 * initialisation order decides whether a lookup table is defined yet is a crash that appears only
 * in a production bundle. The table has no page state, so it belongs here.
 */
/**
 * COLOURS AND LABELS FOR EVERY LIVE STATUS, and only live ones.
 *
 * These two maps went stale when the lifecycle vocabulary was rewritten: they still named
 * `in_flight` and `sent_pending_reception`, which were RETIRED and had their rows migrated away,
 * and they had no entry for any of the statuses that replaced them. A status with no entry falls
 * through to its raw database value, so run history showed `outbound_processing` and
 * `inbound_processing` verbatim while `Send Failed` beside them rendered properly — which reads
 * as a stuck or broken run rather than as the normal "provider accepted, awaiting completion"
 * phase it is.
 *
 * `tests/test_run_status_map_matches_the_vocabulary.py` now derives the required keys from
 * `backend/statuses.py`, so the next vocabulary change fails the build here instead of shipping
 * raw column values to an operator.
 */
export const STATUS_COLORS: Record<string, string> = {
  // Terminal success — greens.
  e2e_success: '#4caf50', e2e_success_degraded: '#9ccc65',
  quality_checked: '#66bb6a',
  // Pre-send — greys, nothing has been attempted yet.
  scheduled: '#b0bec5', held: '#bdbdbd', queued: '#90a4ae',
  // In flight — ambers, deepening as the transmission progresses.
  outbound_requesting: '#ffcc80', outbound_processing: '#ffb74d',
  sending: '#ffa726', inbound_processing: '#4fc3f7',
  // Terminal failure — reds and browns.
  send_failed: '#ef5350', e2e_failure: '#e53935',
  receive_timeout: '#ff8a65', receive_unreadable: '#ff7043',
  manually_failed: '#a1887f',
}

export const STATUS_LABELS: Record<string, string> = {
  e2e_success: 'E2E Success',
  e2e_success_degraded: 'E2E Success (Degraded)',
  quality_checked: 'Completed',
  scheduled: 'Scheduled',
  held: 'Held',
  queued: 'Queued',
  // THE PHASE NAMES AN OPERATOR NEEDS, not the column value. Each of these is an INTERVAL the
  // transmission is inside, and the distinction between them is which side is being waited on:
  // we are waiting to be accepted, the provider is working, the call is on the line, or the
  // fax is out there and the inbound leg is open.
  outbound_requesting: 'Requesting Send',
  outbound_processing: 'Provider Processing',
  sending: 'On the Line',
  inbound_processing: 'Awaiting Reception',
  send_failed: 'Send Failed',
  e2e_failure: 'E2E Failure',
  receive_timeout: 'Receive Timeout',
  receive_unreadable: 'Receive Unreadable',
  manually_failed: 'Manually Failed',
}

export const SLA_LABELS: Record<string, string> = {
  pass: '✓ Pass',
  breach: '⚠️ Breach',
  not_evaluated: '— Not evaluated',
}

export const statusColor = (s: string) => STATUS_COLORS[s] || '#9aa0a6'
export const statusLabel = (s: string) => STATUS_LABELS[s] || s

/**
 * WHETHER `sending` IS A RESTING STATE OR A FAULT — the fields the backend derives.
 *
 * There is no column behind any of these. `backend/services/outbound_settlement.py` derives
 * them from the provider registry on every response, and this file only READS the answer: no
 * provider name, transport name or `polls_outbound_status` check appears here, deliberately.
 * The UI has twice grown a per-provider chain of its own that then had to be kept in step with
 * the backend by hand (`canQuerySrfaxStatus` and its eFax sibling are the surviving pair), and
 * a second copy of a provider decision is one that gets fixed in one place.
 */
export interface OutboundSettlementSource {
  /** `poll` | `send_notification` | `none` | `unrecorded`. */
  outbound_settlement?: string | null
  /** Whether the leg advances out of `sending` without the inbound leg or the SLA. */
  outbound_self_settling?: boolean | null
  /** The short label for a status cell. Null when the leg self-settles. */
  outbound_settlement_marker?: string | null
  /** The full operator sentence. Served on run DETAIL only — never on a list row. */
  outbound_settlement_detail?: string | null
}

/** `none`: nothing will ever report this leg's completion, so `sending` is where it rests. */
export const SETTLEMENT_NONE = 'none'
/** `unrecorded`: what carried this fax is not recorded, so NO claim is made either way. */
export const SETTLEMENT_UNRECORDED = 'unrecorded'

export interface OutboundSettlementDescription {
  /**
   * THE LOUD MARK. True when this row's `sending` needs qualifying rather than reading plainly.
   *
   * Decided by the BACKEND's `outbound_self_settling`, not by re-deriving it here — one
   * definition behind the listing, run detail and the Info panel, so a row marked in one is
   * marked in all three.
   */
  mark: boolean
  /** Short badge for a list cell. Empty when nothing should be shown. */
  badge: string
  /** Tooltip / detail-row text. Empty when nothing should be shown. */
  text: string
}

/**
 * How to read one row's outbound settlement.
 *
 * ONLY MARKED WHILE THE ROW IS ACTUALLY IN `sending`, and that gate is the point rather than a
 * tidiness. On a settled or failed row the answer is history: a fax that plainly arrived does
 * not need to be told that no completion feedback was coming, and a badge on every terminal
 * row of an SMTP job would train the eye to skip the badge — which is how the one row that
 * needed it gets skipped too. The same reasoning the DID mark states for NOT RECORDED.
 *
 * A MISSING FIELD IS NOT A SELF-SETTLING LEG. `outbound_self_settling` is absent on a response
 * from a build that predates it, and `undefined` there must not be read as `false`, which would
 * mark every `sending` row in the listing as needing attention.
 */
export function describeOutboundSettlement(
  src: OutboundSettlementSource & { status?: string | null },
): OutboundSettlementDescription {
  const settling = src.outbound_self_settling
  const marker = src.outbound_settlement_marker || ''
  if (src.status !== 'sending' || settling !== false || !marker) {
    return { mark: false, badge: '', text: '' }
  }
  return {
    mark: true,
    badge: `⏸ ${marker}`,
    // The list row holds no detail paragraph, so the tooltip falls back to the shortest
    // truthful summary rather than to the empty string — a badge with no explanation is the
    // half-fix this whole distinction exists to avoid.
    text:
      src.outbound_settlement_detail ||
      (src.outbound_settlement === SETTLEMENT_UNRECORDED
        ? 'How this leg settles cannot be established from what this row records, so neither ' +
          '"stuck" nor "resting" is being asserted. Open the run for what is known.'
        : 'Nothing will report this leg\u2019s completion, so Sending is its resting state and ' +
          'no action is required. The inbound leg arriving, or the inbound SLA elapsing, is ' +
          'what will move it. Open the run for the full explanation.'),
  }
}
